import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Grid,
  Chip,
  Alert,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import { ArrowBack, Delete } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import SubscriptionAdminService from "../../../services/SubscriptionAdminService";
import SubscriptionPlanService from "../../../services/SubscriptionPlanService";
import { showNotification } from "../../../utils/notifications";

const periodLabel = (p) =>
  p ? `${p.periodCount} ${p.periodUnit.toLowerCase()}${p.periodCount > 1 ? "s" : ""}` : "";

const STATUS_COLORS = {
  ACTIVE: "success",
  FREE: "default",
  PENDING_PAYMENT: "warning",
  EXPIRED: "error",
  CANCELLED: "error",
};

/** ROOT_ADMIN: manage one organisation's subscription, discounts, and billing. */
const OrganizationSubscriptionView = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [assign, setAssign] = useState({ planId: "", planPriceId: "", free: true });
  const [discount, setDiscount] = useState({
    planId: "",
    discountType: "PERCENTAGE",
    percentage: "",
    fixedAmount: "",
    validFrom: "",
    validTo: "",
    notes: "",
    singleUse: false,
  });

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      SubscriptionAdminService.getForOrg(orgId),
      SubscriptionPlanService.listPlans(),
      SubscriptionAdminService.listDiscounts(orgId),
      SubscriptionAdminService.getPayments(orgId),
    ])
      .then(([sub, planList, disc, pays]) => {
        setSubscription(sub);
        setPlans(Array.isArray(planList) ? planList : []);
        setDiscounts(Array.isArray(disc) ? disc : []);
        setPayments(Array.isArray(pays) ? pays : []);
        setLoading(false);
      })
      .catch(() => {
        showNotification("Failed to load subscription details", "error");
        setLoading(false);
      });
  }, [orgId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const selectedPlan = plans.find((p) => p.id === assign.planId);

  const doAssign = () => {
    if (!assign.planId) {
      showNotification("Select a plan", "error");
      return;
    }
    SubscriptionAdminService.assignPlan(orgId, {
      planId: assign.planId,
      planPriceId: assign.planPriceId || null,
      free: assign.free,
    })
      .then(() => {
        showNotification("Plan assigned", "success");
        setAssign({ planId: "", planPriceId: "", free: true });
        loadAll();
      })
      .catch((e) => showNotification(e?.response?.data?.message || "Failed to assign plan", "error"));
  };

  const addDiscount = () => {
    const payload = {
      planId: discount.planId || null,
      discountType: discount.discountType,
      percentage: discount.discountType === "PERCENTAGE" ? Number(discount.percentage) : null,
      fixedAmount: discount.discountType === "FIXED" ? Number(discount.fixedAmount) : null,
      validFrom: discount.validFrom || null,
      validTo: discount.validTo || null,
      notes: discount.notes || null,
      singleUse: discount.singleUse,
    };
    SubscriptionAdminService.createDiscount(orgId, payload)
      .then(() => {
        showNotification("Discount added", "success");
        setDiscount({ planId: "", discountType: "PERCENTAGE", percentage: "", fixedAmount: "", validFrom: "", validTo: "", notes: "", singleUse: false });
        loadAll();
      })
      .catch((e) => showNotification(e?.response?.data?.message || "Failed to add discount", "error"));
  };

  const removeDiscount = (id) => {
    SubscriptionAdminService.deleteDiscount(id)
      .then(() => {
        showNotification("Discount removed", "success");
        loadAll();
      })
      .catch(() => showNotification("Failed to remove discount", "error"));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate("../")} sx={{ mb: 2 }}>
        Back to organisations
      </Button>

      {/* Current subscription */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Current Subscription</Typography>
          {subscription && subscription.planName ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">Plan</Typography>
                <Typography>{subscription.planName} ({subscription.tier})</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box>
                  <Chip
                    size="small"
                    label={subscription.status}
                    color={STATUS_COLORS[subscription.status] || "default"}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">Period</Typography>
                <Typography>
                  {subscription.periodStart || "—"} → {subscription.periodEnd || "∞"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography variant="caption" color="textSecondary">Usage</Typography>
                {(subscription.usage || []).map((u) => (
                  <Typography key={u.featureKey} variant="body2">
                    {u.featureKey}: {u.used}/{u.unlimited ? "∞" : u.limit}
                  </Typography>
                ))}
              </Grid>
            </Grid>
          ) : (
            <Typography color="textSecondary">No subscription (defaults to Free tier).</Typography>
          )}
        </CardContent>
      </Card>

      {/* Assign plan */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Assign / Grant Plan</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                select fullWidth label="Plan" value={assign.planId}
                onChange={(e) => setAssign({ ...assign, planId: e.target.value, planPriceId: "" })}
              >
                {plans.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select fullWidth label="Duration (optional)" value={assign.planPriceId}
                onChange={(e) => setAssign({ ...assign, planPriceId: e.target.value })}
                disabled={!selectedPlan}
              >
                <MenuItem value="">Open-ended</MenuItem>
                {(selectedPlan?.prices || []).map((pr) => (
                  <MenuItem key={pr.id} value={pr.id}>{periodLabel(pr)} ({pr.price} {pr.currency})</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Tooltip title="Admin assignments are always free grants — no charge.">
                <FormControlLabel
                  control={<Switch checked disabled />}
                  label="Free grant"
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button fullWidth variant="contained" onClick={doAssign}>Assign</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Discounts */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Discounts</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Single use</TableCell>
                  <TableCell>Valid</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {discounts.length === 0 && (
                  <TableRow><TableCell colSpan={7} align="center">No discounts.</TableCell></TableRow>
                )}
                {discounts.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.planName || "Any plan"}</TableCell>
                    <TableCell>{d.discountType}</TableCell>
                    <TableCell>
                      {d.discountType === "PERCENTAGE" ? `${d.percentage}%`
                        : d.discountType === "FIXED" ? d.fixedAmount : "Free"}
                    </TableCell>
                    <TableCell>{d.singleUse ? "Yes" : "No"}</TableCell>
                    <TableCell>{(d.validFrom || "—")} → {(d.validTo || "∞")}</TableCell>
                    <TableCell>{d.notes}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => removeDiscount(d.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Add discount</Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                select fullWidth label="Plan" value={discount.planId}
                onChange={(e) => setDiscount({ ...discount, planId: e.target.value })}
              >
                <MenuItem value="">Any plan</MenuItem>
                {plans.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                select fullWidth label="Type" value={discount.discountType}
                onChange={(e) => setDiscount({ ...discount, discountType: e.target.value })}
              >
                <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                <MenuItem value="FIXED">Fixed</MenuItem>
                <MenuItem value="FREE">Free</MenuItem>
              </TextField>
            </Grid>
            {discount.discountType === "PERCENTAGE" && (
              <Grid item xs={6} sm={2}>
                <TextField
                  type="number" fullWidth label="%" value={discount.percentage}
                  onChange={(e) => setDiscount({ ...discount, percentage: e.target.value })}
                />
              </Grid>
            )}
            {discount.discountType === "FIXED" && (
              <Grid item xs={6} sm={2}>
                <TextField
                  type="number" fullWidth label="Amount" value={discount.fixedAmount}
                  onChange={(e) => setDiscount({ ...discount, fixedAmount: e.target.value })}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={2}>
              <TextField
                type="date" fullWidth label="From" InputLabelProps={{ shrink: true }}
                value={discount.validFrom}
                onChange={(e) => setDiscount({ ...discount, validFrom: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                type="date" fullWidth label="To" InputLabelProps={{ shrink: true }}
                value={discount.validTo}
                onChange={(e) => setDiscount({ ...discount, validTo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth label="Notes" value={discount.notes}
                onChange={(e) => setDiscount({ ...discount, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={discount.singleUse}
                    onChange={(e) => setDiscount({ ...discount, singleUse: e.target.checked })}
                  />
                }
                label="Single use"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <Button fullWidth variant="contained" onClick={addDiscount}>Add Discount</Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Billing history */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Billing History</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Period</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center">No payments.</TableCell></TableRow>
                )}
                {payments.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.planName}</TableCell>
                    <TableCell>{p.amount} {p.currency}</TableCell>
                    <TableCell><Chip size="small" label={p.status} /></TableCell>
                    <TableCell>{(p.periodStartApplied || "—")} → {(p.periodEndApplied || "—")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrganizationSubscriptionView;
